import { useState, useEffect, useCallback } from 'react';

const QUEUE_KEY = 'visitex_offline_queue';

export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    } catch { return []; }
  });

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Process queue when coming back online
  useEffect(() => {
    if (isOnline && queue.length > 0) {
      processQueue();
    }
  }, [isOnline]);

  const addToQueue = useCallback((action) => {
    const updated = [...queue, { ...action, id: Date.now(), queued_at: new Date().toISOString() }];
    setQueue(updated);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(updated));
  }, [queue]);

  const processQueue = useCallback(async () => {
    if (queue.length === 0) return;
    const processed = [];
    for (const action of queue) {
      try {
        if (action.type === 'chat_message') {
          const { chatApi } = await import('../services/api');
          await chatApi.sendMessage(action.channel, action.text);
        }
        processed.push(action.id);
      } catch {
        // Keep failed items in queue
      }
    }
    const remaining = queue.filter(a => !processed.includes(a.id));
    setQueue(remaining);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(remaining));
  }, [queue]);

  return { isOnline, queue, addToQueue, processQueue };
}
